import React, { useEffect, useState } from 'react';
import { Table, Button, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useAuth0 } from '@auth0/auth0-react';
import { theme } from '../../../theme';
import { ActionButtons } from '../../common';
import EquipoModal from './EquipoModal';

interface EquipoData {
  id: number;
  descripcion: string;
  sector: {
    id: number;
    descripcion: string;
  };
  alta: string;
}

const desired_output = (data: any, column: any) => {
    const unique_values = data
        .map((item: any) => item[column])
        .filter(
            (value: any, index: any, current_value: any) => current_value.indexOf(value) === index
        );
    return unique_values;
  };

 
const ListarEquipos: React.FC = () => {
  const [data, setData] = useState<EquipoData[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [equipoEditar, setEquipoEditar] = useState<{ id: number; descripcion: string; id_sector: number; alta?: number } | null>(null);

  const { getAccessTokenSilently, isLoading, user } = useAuth0();

  const empresa = user?.id_empresa;
  const sucursal = user?.id_sucursal;
 

const SectorUnicos = desired_output(data, 'sector').map((sector: any) => ({
  text: sector.descripcion, 
  value: sector.id
}));

  const columns: ColumnsType<EquipoData> = [
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>ID</span>,
      dataIndex: 'id',
      key: 'id',
      width: 80,
      align: 'center' as const,
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Descripción</span>,
      dataIndex: 'descripcion',
      key: 'descripcion',
      render: (text: string) => (
        <span style={{ color: theme.colors.textPrimary, fontWeight: 500 }}>{text}</span>
      ),
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Sector</span>,
      dataIndex: ['sector', 'descripcion'],
      key: 'sector',
      filters: SectorUnicos,
      onFilter: (value, record) => record.sector.id === value,
      render: (_, record) => (
        <span style={{ color: theme.colors.textSecondary }}>{record.sector?.descripcion || '-'}</span>
      )
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Estado</span>,
      dataIndex: 'alta',
      key: 'alta',
      align: 'center' as const,
      width: 120,
      render: (value: number) => {
        const isActivo = value === 1;
        return (
          <span style={{ 
            display: 'inline-block',
            padding: '4px 12px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: 600,
            backgroundColor: isActivo ? '#d4edda' : '#f8d7da',
            color: isActivo ? theme.colors.success : theme.colors.error
          }}>
            {isActivo ? 'Activo' : 'Inactivo'}
          </span>
        );
      },
      filters: [
        { text: 'Activo', value: 1 },
        { text: 'Inactivo', value: 0 }
      ],
      onFilter: (value, record) => record.alta === value
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Acciones</span>,
      key: 'acciones',
      align: 'center' as const,
      width: 120,
      render: (_, record) => (
        <ActionButtons
          onEdit={() => handleEditar(record)}
          onDelete={() => handleEliminar(record.id)}
          tooltips={{
            edit: 'Editar equipo',
            delete: 'Eliminar equipo'
          }}
        />
      ),
    }
  ];

  const fetchEquipos = async () => {
    setLoading(true);
    try {
      const accessToken = await getAccessTokenSilently();
      const response = await fetch(`${process.env.REACT_APP_INSUMOS_URL}/listar_equipos/${empresa}/${sucursal}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Error al cargar los equipos');
      }
      const result = await response.json();
      
      setData(result);
    } catch (error) {
      console.error('Error completo:', error);
      message.error('Error al cargar los equipos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isLoading) {
      fetchEquipos();
    }
  }, [isLoading, empresa, sucursal]);

  const handleNuevo = () => {
    setEquipoEditar(null);
    setModalVisible(true);
  };

  const handleEditar = (equipo: EquipoData) => {
    setEquipoEditar({ 
      id: equipo.id, 
      descripcion: equipo.descripcion,
      id_sector: equipo.sector.id,
      alta: Number(equipo.alta)
    });
    setModalVisible(true);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setEquipoEditar(null);
  };

  const handleModalSuccess = () => {
    fetchEquipos(); // Recargar lista
  };

  const handleEliminar = async (id: number) => {
    if (window.confirm('¿Está seguro de que desea eliminar este equipo?')) {
      try {
        const accessToken = await getAccessTokenSilently();
        const response = await fetch(`${process.env.REACT_APP_INSUMOS_URL}/eliminar_equipo/${id}/${empresa}/${sucursal}/`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        });
        
        if (!response.ok) {
          throw new Error('Error al eliminar el equipo');
        }
        
        message.success('Equipo eliminado correctamente');
        fetchEquipos(); // Recargar datos
      } catch (error) {
        console.error('Error al eliminar:', error);
        message.error('Error al eliminar el equipo');
      }
    }
  };

  return (
    <div>
      {/* Botón Nuevo arriba */}
      <div style={{ marginBottom: '16px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
        <Button 
          type='primary'
          onClick={handleNuevo}
          style={{
            backgroundColor: theme.colors.secondary,
            borderColor: theme.colors.secondary,
            fontWeight: 500
          }}
        >
          Nuevo Equipo
        </Button>
      </div>
      
      {/* Tabla con acciones inline */}
      <Table
        scroll={{ x: 'max-content' }}
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={{
          pageSize: 10,
          total: data.length,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} registros`,
        }}
      />

      {/* Modal para crear/editar */}
      <EquipoModal
        visible={modalVisible}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
        equipo={equipoEditar}
      />
    </div>
  );
};

export default ListarEquipos; 